import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAdminEvent } from "@/context/AppContext";
import { api } from "@/lib/api";
import { StatCard, PageIntro, Card } from "@/admin/ui";
import {
  Trophy, Plus, QrCode, Camera, MapPin, HelpCircle, Gift, CalendarClock,
  Search, X, ChevronRight, Check, Pencil, Copy, Archive, Play, Pause, Square,
  Trash2, Eye, RefreshCw, Sparkles, Users, AlertTriangle, Clock, FileText, Star,
  Medal, Snowflake, Download, Crown, Award, Lock, Compass, Brain, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

// ---- vocabulary / presentation --------------------------------------------
const TYPES = [
  { key: "QR_CHECKIN", label: "QR Check-in", icon: QrCode, hint: "Scan a QR at a location to check in." },
  { key: "ACTIVITY", label: "Activity / Attendance", icon: CalendarClock, hint: "Attend a scheduled activity." },
  { key: "TRIVIA_POLL", label: "Trivia / Poll", icon: HelpCircle, hint: "Answer a question or take part in a poll." },
  { key: "PHOTO", label: "Photo Challenge", icon: Camera, hint: "Submit a photo (review comes later)." },
  { key: "EXPLORE", label: "Explore / Scavenger", icon: MapPin, hint: "Find something and prove it." },
  { key: "BONUS", label: "Bonus / Admin", icon: Gift, hint: "Manually awarded by a manager." },
];
const TYPE_MAP = Object.fromEntries(TYPES.map((t) => [t.key, t]));
const CATEGORIES = [
  { key: "pc_winner", label: "PC Winner" },
  { key: "mdc_winner", label: "MDC Winner" },
  { key: "non_pc", label: "Non-PC" },
];

const RUNTIME_BADGE = {
  LIVE: "bg-[hsl(var(--mint))] text-[hsl(var(--primary))]",
  SCHEDULED: "bg-[hsl(var(--secondary)/0.15)] text-[hsl(var(--secondary))]",
  PAUSED: "bg-[hsl(38_70%_92%)] text-[hsl(38_50%_32%)]",
  ENDED: "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]",
};
const PUB_BADGE = {
  DRAFT: "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]",
  PUBLISHED: "bg-[hsl(var(--mint))] text-[hsl(var(--primary))]",
  ARCHIVED: "bg-[hsl(var(--pc-gold-2)/0.25)] text-[hsl(38_40%_32%)]",
};

const FILTERS = ["All", "Draft", "Scheduled", "Live", "Paused", "Ended", "Archived"];

function StatusBadges({ c }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      <span data-testid="challenge-pub-state" className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${PUB_BADGE[c.publication_state] || PUB_BADGE.DRAFT}`}>{c.publication_state}</span>
      {c.runtime_state && <span data-testid="challenge-runtime-state" className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${RUNTIME_BADGE[c.runtime_state] || ""}`}>{c.runtime_state}</span>}
      {c.mystery_enabled && <span className="text-[10px] font-bold rounded-full px-2 py-0.5 bg-[hsl(var(--secondary)/0.12)] text-[hsl(var(--secondary))] inline-flex items-center gap-1"><Sparkles className="h-3 w-3" />Mystery</span>}
    </div>
  );
}

const fmtDT = (s) => (s ? String(s).replace("T", " ").slice(0, 16) : "—");

// ===========================================================================
export default function Gamification() {
  const { eventId } = useAdminEvent();
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [audit, setAudit] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wizard, setWizard] = useState(null); // {mode:'create'|'edit', challengeId}

  const load = async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const [ov, ch, au, st] = await Promise.all([
        api.gamOverview(eventId), api.gamChallenges(eventId),
        api.gamAudit(eventId), api.gamSettings(eventId),
      ]);
      setOverview(ov); setChallenges(ch.challenges || []); setAudit(au.audit || []); setSettings(st);
    } catch (e) { toast.error("Failed to load gamification"); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eventId]);

  const TABS = [
    { key: "overview", label: "Overview" },
    { key: "challenges", label: "Challenges" },
    { key: "leaderboard", label: "Leaderboard" },
    { key: "badges", label: "Badges" },
    { key: "activity", label: "Activity Log" },
    { key: "settings", label: "Settings" },
  ];

  return (
    <div data-testid="gamification-module">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-serif text-2xl text-[hsl(var(--primary))] flex items-center gap-2"><Trophy className="h-6 w-6 text-[hsl(var(--pc-gold))]" /> Gamification</h2>
          <PageIntro>Configure event challenges, points and audiences. Attendee gameplay goes live in a later phase — nothing here is exposed to attendees yet.</PageIntro>
        </div>
        <button data-testid="gam-create-challenge-top" onClick={() => setWizard({ mode: "create" })} className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] text-white px-4 py-2 text-sm font-semibold hover:opacity-90 transition-[opacity]"><Plus className="h-4 w-4" /> Create Challenge</button>
      </div>

      <div className="flex gap-1 border-b border-[hsl(var(--border))] mt-2 mb-5 overflow-x-auto no-scrollbar">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`gam-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === t.key ? "border-[hsl(var(--secondary))] text-[hsl(var(--primary))]" : "border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]"}`}>{t.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{[0,1,2,3].map((i) => <div key={i} className="premium-card p-4 h-24 animate-pulse bg-[hsl(var(--muted)/0.4)]" />)}</div>
      ) : (
        <>
          {tab === "overview" && <OverviewTab overview={overview} onCreate={() => setWizard({ mode: "create" })} onOpen={(id) => setWizard({ mode: "edit", challengeId: id })} />}
          {tab === "challenges" && <ChallengesTab eventId={eventId} challenges={challenges} onCreate={() => setWizard({ mode: "create" })} onOpen={(id) => setWizard({ mode: "edit", challengeId: id })} reload={load} />}
          {tab === "leaderboard" && <LeaderboardTab eventId={eventId} />}
          {tab === "badges" && <BadgesTab eventId={eventId} challenges={challenges} />}
          {tab === "activity" && <ActivityLogTab audit={audit} />}
          {tab === "settings" && <SettingsTab eventId={eventId} settings={settings} onSaved={load} />}
        </>
      )}

      {wizard && <ChallengeWizard eventId={eventId} mode={wizard.mode} challengeId={wizard.challengeId} onClose={() => setWizard(null)} onSaved={() => { setWizard(null); load(); }} />}
    </div>
  );
}

// ---- Overview --------------------------------------------------------------
function OverviewTab({ overview, onCreate, onOpen }) {
  if (!overview) return null;
  const c = overview.counts || {};
  const cards = [
    { label: "Total Challenges", value: c.total, icon: Trophy, accent: "gold" },
    { label: "Draft", value: c.draft, icon: FileText },
    { label: "Scheduled", value: c.scheduled, icon: Clock },
    { label: "Live", value: c.live, icon: Play },
    { label: "Paused", value: c.paused, icon: Pause },
    { label: "Ended", value: c.ended, icon: Square },
    { label: "Archived", value: c.archived, icon: Archive },
    { label: "Eligible Attendees", value: overview.audience_reach?.everyone, icon: Users },
  ];
  const bd = overview.badges || {};
  const badgeCards = [
    { label: "Active Badges", value: bd.active_badges, icon: Medal, accent: "gold" },
    { label: "Badges Awarded", value: bd.badges_awarded, icon: Award },
    { label: "Unique Earners", value: bd.unique_earners, icon: Users },
  ];
  return (
    <div className="space-y-5" data-testid="gam-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map((k) => <StatCard key={k.label} label={k.label} value={k.value ?? 0} icon={k.icon} accent={k.accent} />)}
      </div>
      <div className="grid grid-cols-3 gap-3" data-testid="gam-overview-badges">
        {badgeCards.map((k) => <StatCard key={k.label} label={k.label} value={k.value ?? 0} icon={k.icon} accent={k.accent} />)}
      </div>

      {overview.warnings?.length > 0 && (
        <Card>
          <div className="text-[11px] uppercase font-semibold text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5 text-[hsl(38_60%_45%)]" /> Configuration warnings ({overview.warnings.length})</div>
          <div className="space-y-1.5" data-testid="gam-warnings">
            {overview.warnings.map((w, i) => (
              <div key={i} className="text-sm flex items-center gap-2"><span className="font-medium text-[hsl(var(--primary))]">{w.name}</span><span className="text-[hsl(var(--muted-foreground))]">— {w.warning}</span></div>
            ))}
          </div>
        </Card>
      )}

      <Card>
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] uppercase font-semibold text-[hsl(var(--muted-foreground))]">Recently edited / published</div>
          <button data-testid="gam-overview-create" onClick={onCreate} className="text-sm font-semibold text-[hsl(var(--secondary))] inline-flex items-center gap-1"><Plus className="h-4 w-4" /> Create Challenge</button>
        </div>
        {overview.recent?.length ? (
          <div className="divide-y divide-[hsl(var(--border))]">
            {overview.recent.map((r) => {
              const T = TYPE_MAP[r.challenge_type] || {};
              const Icon = T.icon || Trophy;
              return (
                <button key={r.id} data-testid="gam-recent-item" onClick={() => onOpen(r.id)} className="w-full flex items-center justify-between gap-3 py-2.5 text-left hover:bg-[hsl(var(--muted)/0.4)] rounded-lg px-2 -mx-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-8 w-8 rounded-lg bg-[hsl(var(--secondary)/0.12)] text-[hsl(var(--secondary))] flex items-center justify-center shrink-0"><Icon className="h-4 w-4" /></div>
                    <div className="min-w-0"><div className="text-sm font-semibold text-[hsl(var(--primary))] truncate">{r.name || "Untitled"}</div><div className="text-xs text-[hsl(var(--muted-foreground))]">{T.label} · {r.points ?? 0} pts</div></div>
                  </div>
                  <StatusBadges c={r} />
                </button>
              );
            })}
          </div>
        ) : <div className="text-sm text-[hsl(var(--muted-foreground))]">No challenges yet. Create your first challenge to get started.</div>}
        <div className="text-xs text-[hsl(var(--muted-foreground))] mt-3">Participation and points totals will appear here once attendee gameplay is enabled in a later phase.</div>
      </Card>
    </div>
  );
}

// ---- Challenges table ------------------------------------------------------
function ChallengesTab({ eventId, challenges, onCreate, onOpen, reload }) {
  const [filter, setFilter] = useState("All");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  const rows = useMemo(() => {
    return challenges.filter((c) => {
      if (q && !(c.name || "").toLowerCase().includes(q.toLowerCase())) return false;
      if (filter === "All") return true;
      if (filter === "Draft") return c.publication_state === "DRAFT";
      if (filter === "Archived") return c.publication_state === "ARCHIVED";
      return c.runtime_state === filter.toUpperCase();
    });
  }, [challenges, filter, q]);

  const act = async (fn, id, label) => {
    setBusy(id);
    try { await fn(eventId, id); toast.success(label); await reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || `${label} failed`); }
    setBusy(null);
  };

  return (
    <div className="space-y-4" data-testid="gam-challenges">
      <div className="flex items-center gap-2 flex-wrap justify-between">
        <div className="flex gap-1 flex-wrap">
          {FILTERS.map((f) => (
            <button key={f} data-testid={`gam-filter-${f.toLowerCase()}`} onClick={() => setFilter(f)}
              className={`text-xs font-semibold rounded-full px-3 py-1.5 ${filter === f ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]"}`}>{f}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-[hsl(var(--muted-foreground))]" />
            <input data-testid="gam-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search challenges" className="pl-8 pr-3 py-2 text-sm rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] w-52" />
          </div>
          <button data-testid="gam-create-challenge" onClick={onCreate} className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] text-white px-3 py-2 text-sm font-semibold hover:opacity-90"><Plus className="h-4 w-4" /> Create</button>
        </div>
      </div>

      {rows.length === 0 ? (
        <Card className="text-center py-10"><div className="text-sm text-[hsl(var(--muted-foreground))]">No challenges match this view.</div></Card>
      ) : (
        <div className="premium-card overflow-hidden p-0">
          <table className="w-full text-sm" data-testid="gam-challenges-table">
            <thead className="bg-[hsl(var(--muted)/0.5)] text-[hsl(var(--muted-foreground))]">
              <tr className="text-left text-[11px] uppercase tracking-wide">
                <th className="px-4 py-2.5 font-semibold">Challenge</th>
                <th className="px-3 py-2.5 font-semibold">Type</th>
                <th className="px-3 py-2.5 font-semibold">Points</th>
                <th className="px-3 py-2.5 font-semibold">Audience</th>
                <th className="px-3 py-2.5 font-semibold">Availability</th>
                <th className="px-3 py-2.5 font-semibold">Status</th>
                <th className="px-3 py-2.5 font-semibold">Ver</th>
                <th className="px-3 py-2.5 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[hsl(var(--border))]">
              {rows.map((c) => {
                const T = TYPE_MAP[c.challenge_type] || {};
                const Icon = T.icon || Trophy;
                const aud = c.audience_mode === "EVERYONE" ? "Everyone" : c.audience_mode === "CATEGORIES" ? "Categories" : "Selected";
                return (
                  <tr key={c.id} data-testid="gam-challenge-row" className="hover:bg-[hsl(var(--muted)/0.3)]">
                    <td className="px-4 py-3">
                      <button onClick={() => onOpen(c.id)} className="flex items-center gap-2 text-left">
                        <div className="h-8 w-8 rounded-lg bg-[hsl(var(--secondary)/0.12)] text-[hsl(var(--secondary))] flex items-center justify-center shrink-0"><Icon className="h-4 w-4" /></div>
                        <span className="font-semibold text-[hsl(var(--primary))] flex items-center gap-1">{c.name || "Untitled"}{c.featured && <Star className="h-3 w-3 text-[hsl(var(--pc-gold))]" />}</span>
                      </button>
                    </td>
                    <td className="px-3 py-3 text-[hsl(var(--muted-foreground))]">{T.label}</td>
                    <td className="px-3 py-3 font-semibold text-[hsl(var(--primary))]">{c.points ?? 0}</td>
                    <td className="px-3 py-3 text-[hsl(var(--muted-foreground))]">{aud} · {c.eligible_count}</td>
                    <td className="px-3 py-3 text-xs text-[hsl(var(--muted-foreground))]">{c.start_at || c.end_at ? `${fmtDT(c.start_at)} → ${fmtDT(c.end_at)}` : "Always"}</td>
                    <td className="px-3 py-3"><StatusBadges c={c} /></td>
                    <td className="px-3 py-3 text-xs text-[hsl(var(--muted-foreground))]">{c.current_version_number ? `v${c.current_version_number}` : "—"}{c.has_draft ? ` (+d${c.draft_version_number})` : ""}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <IconBtn tip="Edit" testid="gam-action-edit" onClick={() => onOpen(c.id)}><Pencil className="h-3.5 w-3.5" /></IconBtn>
                        {c.publication_state === "DRAFT" && <IconBtn tip="Publish" testid="gam-action-publish" onClick={() => act(api.gamPublish, c.id, "Published")} disabled={busy === c.id}><Check className="h-3.5 w-3.5" /></IconBtn>}
                        {c.has_draft && c.publication_state === "PUBLISHED" && <IconBtn tip="Publish Update" testid="gam-action-publish-update" onClick={() => act(api.gamPublish, c.id, "Update published")} disabled={busy === c.id}><Check className="h-3.5 w-3.5" /></IconBtn>}
                        {c.runtime_state === "LIVE" && <IconBtn tip="Pause" testid="gam-action-pause" onClick={() => act(api.gamPause, c.id, "Paused")} disabled={busy === c.id}><Pause className="h-3.5 w-3.5" /></IconBtn>}
                        {c.runtime_state === "PAUSED" && <IconBtn tip="Resume" testid="gam-action-resume" onClick={() => act(api.gamResume, c.id, "Resumed")} disabled={busy === c.id}><Play className="h-3.5 w-3.5" /></IconBtn>}
                        {c.publication_state === "PUBLISHED" && c.runtime_state !== "ENDED" && <IconBtn tip="End" testid="gam-action-end" onClick={() => act(api.gamEnd, c.id, "Ended")} disabled={busy === c.id}><Square className="h-3.5 w-3.5" /></IconBtn>}
                        <IconBtn tip="Duplicate" testid="gam-action-duplicate" onClick={() => act(api.gamDuplicate, c.id, "Duplicated")} disabled={busy === c.id}><Copy className="h-3.5 w-3.5" /></IconBtn>
                        {c.publication_state === "DRAFT" && !c.current_version_number
                          ? <IconBtn tip="Delete draft" testid="gam-action-delete" onClick={() => act(api.gamDeleteChallenge, c.id, "Draft deleted")} disabled={busy === c.id}><Trash2 className="h-3.5 w-3.5" /></IconBtn>
                          : c.publication_state !== "ARCHIVED" && <IconBtn tip="Archive" testid="gam-action-archive" onClick={() => act(api.gamArchive, c.id, "Archived")} disabled={busy === c.id}><Archive className="h-3.5 w-3.5" /></IconBtn>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function IconBtn({ children, tip, onClick, disabled, testid }) {
  return (
    <button data-testid={testid} title={tip} onClick={onClick} disabled={disabled}
      className="h-7 w-7 inline-flex items-center justify-center rounded-lg text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted)/0.7)] hover:text-[hsl(var(--primary))] disabled:opacity-40">{children}</button>
  );
}

// ---- Activity Log ----------------------------------------------------------
function ActivityLogTab({ audit }) {
  return (
    <Card data-testid="gam-activity-log">
      <div className="text-[11px] uppercase font-semibold text-[hsl(var(--muted-foreground))] mb-3">Configuration activity ({audit.length})</div>
      {audit.length === 0 ? <div className="text-sm text-[hsl(var(--muted-foreground))]">No activity yet.</div> : (
        <div className="divide-y divide-[hsl(var(--border))]">
          {audit.map((a) => (
            <div key={a.id} data-testid="gam-audit-row" className="py-2.5 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-[hsl(var(--primary))]"><span className="font-semibold">{(a.action || "").replace(/_/g, " ")}</span>{a.summary ? ` — ${a.summary}` : ""}</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">{a.object_type} · {a.actor}</div>
              </div>
              <div className="text-xs text-[hsl(var(--muted-foreground))] whitespace-nowrap">{fmtDT(a.created_at)}</div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ---- Settings --------------------------------------------------------------
function SettingsTab({ eventId, settings, onSaved }) {
  const [f, setF] = useState(settings || {});
  const [busy, setBusy] = useState(false);
  useEffect(() => setF(settings || {}), [settings]);
  const lb = f.leaderboard || {};
  const setLb = (k, v) => setF({ ...f, leaderboard: { ...lb, [k]: v } });
  const save = async () => {
    setBusy(true);
    try { await api.gamUpdateSettings(eventId, { enabled: f.enabled, attendee_visibility_enabled: f.attendee_visibility_enabled, leaderboard: lb }); toast.success("Settings saved"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };
  return (
    <div className="space-y-4 max-w-2xl" data-testid="gam-settings">
      <Card>
        <div className="font-serif text-lg text-[hsl(var(--primary))] mb-3">Gamification</div>
        <ToggleRow testid="gam-setting-enabled" label="Gamification enabled" desc="Master switch for this event's gamification configuration." checked={!!f.enabled} onChange={(v) => setF({ ...f, enabled: v })} />
        <ToggleRow testid="gam-setting-attendee-visibility" label="Attendee visibility" desc="Kept OFF in this phase — challenges are not shown in the attendee app yet." checked={!!f.attendee_visibility_enabled} onChange={(v) => setF({ ...f, attendee_visibility_enabled: v })} />
      </Card>
      <Card>
        <div className="font-serif text-lg text-[hsl(var(--primary))] mb-1">Leaderboard defaults</div>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">Stored for a future phase. The attendee leaderboard is not built in this phase.</p>
        <label className="block text-sm font-medium text-[hsl(var(--primary))] mb-1">Visibility mode</label>
        <select data-testid="gam-setting-lb-mode" value={lb.visibility_mode || "TOP_10_AND_MY_RANK"} onChange={(e) => setLb("visibility_mode", e.target.value)} className="w-full rounded-lg border border-[hsl(var(--border))] px-3 py-2 text-sm mb-3 bg-[hsl(var(--background))]">
          <option value="TOP_10_AND_MY_RANK">Top 10 + My Rank (recommended)</option>
          <option value="FULL">Full</option>
          <option value="MY_RANK_ONLY">My Rank Only</option>
          <option value="HIDDEN">Hidden</option>
        </select>
        <ToggleRow testid="gam-setting-show-points" label="Show points" checked={lb.show_points !== false} onChange={(v) => setLb("show_points", v)} />
        <ToggleRow testid="gam-setting-show-rank" label="Show rank" checked={lb.show_rank !== false} onChange={(v) => setLb("show_rank", v)} />
        <div className="text-xs text-[hsl(var(--muted-foreground))] mt-2">Tie-break order: {(lb.tie_break || ["total_points", "challenge_completions", "earliest_final_score"]).join(" → ")}</div>
      </Card>
      <button data-testid="gam-settings-save" onClick={save} disabled={busy} className="rounded-full bg-[hsl(var(--primary))] text-white px-5 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50">{busy ? "Saving…" : "Save settings"}</button>
    </div>
  );
}

function ToggleRow({ label, desc, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <div><div className="text-sm font-medium text-[hsl(var(--primary))]">{label}</div>{desc && <div className="text-xs text-[hsl(var(--muted-foreground))]">{desc}</div>}</div>
      <button data-testid={testid} role="switch" aria-checked={checked} onClick={() => onChange(!checked)} className={`relative h-6 w-11 rounded-full transition-colors shrink-0 ${checked ? "bg-[hsl(var(--secondary))]" : "bg-[hsl(var(--muted))]"}`}>
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
      </button>
    </div>
  );
}

// ---- Wizard ----------------------------------------------------------------
const emptyForm = {
  name: "", short_description: "", full_instructions: "", challenge_type: "QR_CHECKIN",
  points: 50, icon: "", audience_mode: "EVERYONE", eligible_categories: [], selected_attendee_ids: [],
  start_at: "", end_at: "", mystery_enabled: false, reveal_at: "",
  featured: false, display_order: 0, leaderboard_eligible: true, show_points: true,
  max_completions: 1, linked_activity_id: "", completion: { method: "qr" },
};

function ChallengeWizard({ eventId, mode, challengeId, onClose, onSaved }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(emptyForm);
  const [challenge, setChallenge] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [elig, setElig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(mode === "create");
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setC = (patch) => setForm((f) => ({ ...f, completion: { ...f.completion, ...patch } }));

  useEffect(() => {
    api.gamSessions(eventId).then((r) => setSessions(r.sessions || [])).catch(() => {});
    if (mode === "edit" && challengeId) {
      api.gamChallenge(eventId, challengeId).then((ch) => {
        setChallenge(ch);
        const v = ch.effective_version || {};
        setForm({ ...emptyForm, ...v, completion: v.completion || {}, start_at: (v.start_at || "").slice(0, 16), end_at: (v.end_at || "").slice(0, 16), reveal_at: (v.reveal_at || "").slice(0, 16) });
        setLoaded(true);
      }).catch(() => { toast.error("Failed to load challenge"); onClose(); });
    }
  }, [eventId, mode, challengeId]); // eslint-disable-line

  // eligibility preview
  useEffect(() => {
    const body = { audience_mode: form.audience_mode, eligible_categories: form.eligible_categories, selected_attendee_ids: form.selected_attendee_ids };
    api.gamEligibility(eventId, body).then(setElig).catch(() => setElig(null));
  }, [eventId, form.audience_mode, JSON.stringify(form.eligible_categories), JSON.stringify(form.selected_attendee_ids)]); // eslint-disable-line

  const isEdit = mode === "edit";
  const isPublished = challenge?.publication_state === "PUBLISHED";
  const isQrType = ["QR_CHECKIN", "EXPLORE", "ACTIVITY"].includes(form.challenge_type);

  const buildBody = () => ({
    ...form,
    points: Number(form.points) || 0,
    display_order: Number(form.display_order) || 0,
    max_completions: Number(form.max_completions) || 1,
    start_at: form.start_at || null, end_at: form.end_at || null, reveal_at: form.reveal_at || null,
  });

  const saveDraft = async () => {
    setBusy(true);
    try {
      let ch;
      if (!challenge) { ch = await api.gamCreateChallenge(eventId, buildBody()); setChallenge(ch); }
      else { ch = await api.gamPatchChallenge(eventId, challenge.id, buildBody()); setChallenge(ch); }
      toast.success("Draft saved");
      return ch;
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); throw e; }
    finally { setBusy(false); }
  };

  const publish = async () => {
    try { const ch = await saveDraft(); await api.gamPublish(eventId, ch.id); toast.success("Challenge published"); onSaved(); }
    catch (e) { /* handled */ }
  };

  const STEPS = ["Basics", "Completion", "Audience", "Availability", "Display", "Review"];

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" data-testid="gam-wizard">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl bg-[hsl(var(--background))] shadow-xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[hsl(var(--border))]">
          <div>
            <div className="font-serif text-xl text-[hsl(var(--primary))]">{isEdit ? "Edit Challenge" : "Create Challenge"}{isPublished && <span className="ml-2 text-xs font-semibold text-[hsl(var(--secondary))]">Published v{challenge?.current_version?.version_number}{challenge?.draft_version ? ` · Draft v${challenge.draft_version.version_number}` : ""}</span>}</div>
            <div className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-1 mt-0.5 flex-wrap">
              {STEPS.map((s, i) => (
                <span key={s} className="flex items-center gap-1"><span className={step === i + 1 ? "text-[hsl(var(--secondary))] font-semibold" : ""}>{s}</span>{i < STEPS.length - 1 && <ChevronRight className="h-3 w-3" />}</span>
              ))}
            </div>
          </div>
          <button data-testid="gam-wizard-close" onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center rounded-lg hover:bg-[hsl(var(--muted)/0.6)]"><X className="h-4 w-4" /></button>
        </div>

        <div className="p-5 overflow-y-auto no-scrollbar flex-1">
          {!loaded ? <div className="p-10 text-center text-sm text-[hsl(var(--muted-foreground))]">Loading…</div> : (
          <>
          {step === 1 && (
            <div className="space-y-4" data-testid="gam-step-basics">
              <Field label="Challenge name"><input data-testid="gam-field-name" value={form.name} onChange={(e) => set({ name: e.target.value })} className={inp} placeholder="e.g. Welcome to Cabo" /></Field>
              <Field label="Short description"><input data-testid="gam-field-short" value={form.short_description || ""} onChange={(e) => set({ short_description: e.target.value })} className={inp} placeholder="One line shown on the card" /></Field>
              <Field label="Full instructions"><textarea data-testid="gam-field-instructions" value={form.full_instructions || ""} onChange={(e) => set({ full_instructions: e.target.value })} className={`${inp} h-24`} placeholder="What the attendee needs to do" /></Field>
              <Field label="Challenge type">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {TYPES.map((t) => {
                    const Icon = t.icon; const active = form.challenge_type === t.key;
                    return (
                      <button key={t.key} data-testid={`gam-type-${t.key}`} disabled={isEdit} onClick={() => set({ challenge_type: t.key, completion: defaultCompletion(t.key) })}
                        className={`text-left rounded-xl border p-3 transition-colors disabled:opacity-60 ${active ? "border-[hsl(var(--secondary))] bg-[hsl(var(--secondary)/0.08)]" : "border-[hsl(var(--border))] hover:border-[hsl(var(--secondary)/0.5)]"}`}>
                        <Icon className="h-5 w-5 text-[hsl(var(--secondary))]" /><div className="text-sm font-semibold text-[hsl(var(--primary))] mt-1">{t.label}</div><div className="text-[11px] text-[hsl(var(--muted-foreground))]">{t.hint}</div>
                      </button>
                    );
                  })}
                </div>
                {isEdit && <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">Challenge type cannot be changed after creation.</div>}
              </Field>
              <Field label="Points"><input data-testid="gam-field-points" type="number" min="0" value={form.points} onChange={(e) => set({ points: e.target.value })} className={inp} /></Field>
            </div>
          )}

          {step === 2 && <CompletionStep form={form} set={set} setC={setC} sessions={sessions} challenge={challenge} eventId={eventId} />}

          {step === 3 && (
            <div className="space-y-4" data-testid="gam-step-audience">
              <Field label="Audience">
                {[["EVERYONE", "Everyone"], ["CATEGORIES", "Attendee categories"], ["SELECTED", "Selected attendees"]].map(([k, l]) => (
                  <label key={k} className="flex items-center gap-2 py-1 cursor-pointer">
                    <input type="radio" data-testid={`gam-audience-${k}`} name="aud" checked={form.audience_mode === k} onChange={() => set({ audience_mode: k })} />
                    <span className="text-sm text-[hsl(var(--primary))]">{l}</span>
                  </label>
                ))}
              </Field>
              {form.audience_mode === "CATEGORIES" && (
                <div className="flex flex-wrap gap-2" data-testid="gam-audience-categories">
                  {CATEGORIES.map((c) => {
                    const on = form.eligible_categories.includes(c.key);
                    return <button key={c.key} data-testid={`gam-cat-${c.key}`} onClick={() => set({ eligible_categories: on ? form.eligible_categories.filter((x) => x !== c.key) : [...form.eligible_categories, c.key] })}
                      className={`text-xs font-semibold rounded-full px-3 py-1.5 ${on ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"}`}>{c.label}</button>;
                  })}
                </div>
              )}
              {form.audience_mode === "SELECTED" && (
                <Field label="Attendee IDs (comma-separated)"><textarea data-testid="gam-selected-ids" value={(form.selected_attendee_ids || []).join(", ")} onChange={(e) => set({ selected_attendee_ids: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className={`${inp} h-20`} placeholder="att-... , att-..." /></Field>
              )}
              <div className="premium-card p-3 flex items-center gap-2" data-testid="gam-eligibility-count">
                <Users className="h-4 w-4 text-[hsl(var(--secondary))]" />
                <span className="text-sm text-[hsl(var(--primary))] font-semibold">{elig ? elig.eligible_count : "…"}</span>
                <span className="text-sm text-[hsl(var(--muted-foreground))]">eligible attendees</span>
                {elig?.invalid_ids?.length > 0 && <span className="text-xs text-[hsl(38_60%_45%)] ml-2">{elig.invalid_ids.length} invalid id(s)</span>}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4" data-testid="gam-step-availability">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Start"><input data-testid="gam-field-start" type="datetime-local" value={form.start_at || ""} onChange={(e) => set({ start_at: e.target.value })} className={inp} /></Field>
                <Field label="End"><input data-testid="gam-field-end" type="datetime-local" value={form.end_at || ""} onChange={(e) => set({ end_at: e.target.value })} className={inp} /></Field>
              </div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">Times are in the event timezone ({challenge?.effective_version?.timezone || "event local"}). Leave empty for an always-available challenge.</div>
              <ToggleRow testid="gam-field-mystery" label="Mystery challenge" desc="Hide the challenge content until a reveal time." checked={form.mystery_enabled} onChange={(v) => set({ mystery_enabled: v })} />
              {form.mystery_enabled && <Field label="Reveal at"><input data-testid="gam-field-reveal" type="datetime-local" value={form.reveal_at || ""} onChange={(e) => set({ reveal_at: e.target.value })} className={inp} /></Field>}
            </div>
          )}

          {step === 5 && (
            <div className="space-y-4" data-testid="gam-step-display">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Points"><input data-testid="gam-field-points-2" type="number" min="0" value={form.points} onChange={(e) => set({ points: e.target.value })} className={inp} /></Field>
                <Field label="Display order"><input data-testid="gam-field-order" type="number" value={form.display_order} onChange={(e) => set({ display_order: e.target.value })} className={inp} /></Field>
              </div>
              <ToggleRow testid="gam-field-leaderboard" label="Leaderboard eligible" checked={form.leaderboard_eligible} onChange={(v) => set({ leaderboard_eligible: v })} />
              <ToggleRow testid="gam-field-featured" label="Featured" checked={form.featured} onChange={(v) => set({ featured: v })} />
              <ToggleRow testid="gam-field-show-points" label="Show points before completion" checked={form.show_points} onChange={(v) => set({ show_points: v })} />
            </div>
          )}

          {step === 6 && <ReviewStep form={form} elig={elig} challenge={challenge} eventId={eventId} isQrType={isQrType} sessions={sessions} />}
          </>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-4 border-t border-[hsl(var(--border))]">
          <button disabled={step === 1} onClick={() => setStep((s) => Math.max(1, s - 1))} className="text-sm font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] disabled:opacity-40">Back</button>
          <div className="flex items-center gap-2">
            <button data-testid="gam-wizard-save-draft" onClick={saveDraft} disabled={busy} className="rounded-full border border-[hsl(var(--border))] px-4 py-2 text-sm font-semibold text-[hsl(var(--primary))] hover:bg-[hsl(var(--muted)/0.5)] disabled:opacity-50">Save Draft</button>
            {step < 6 ? (
              <button data-testid="gam-wizard-next" onClick={() => setStep((s) => Math.min(6, s + 1))} className="rounded-full bg-[hsl(var(--primary))] text-white px-4 py-2 text-sm font-semibold hover:opacity-90">Next</button>
            ) : (
              <button data-testid="gam-wizard-publish" onClick={publish} disabled={busy} className="rounded-full bg-[hsl(var(--secondary))] text-white px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50">{isPublished ? "Publish Update" : "Publish"}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const inp = "w-full rounded-lg border border-[hsl(var(--border))] px-3 py-2 text-sm bg-[hsl(var(--background))]";
function Field({ label, children }) {
  return <div><label className="block text-sm font-medium text-[hsl(var(--primary))] mb-1">{label}</label>{children}</div>;
}
function defaultCompletion(type) {
  if (type === "TRIVIA_POLL") return { mode: "TRIVIA", question: "", options: ["", ""], correct_option_index: 0, max_attempts: 1, show_correct_after: true };
  if (type === "PHOTO") return { approval_mode: "ADMIN_APPROVAL", resubmission_allowed: false, caption_required: false };
  if (type === "EXPLORE") return { method: "qr" };
  if (type === "ACTIVITY") return { method: "activity" };
  if (type === "BONUS") return { allow_multiple_attendees: false };
  return { method: "qr" };
}

// ---- Completion step (dynamic per type) ------------------------------------
function CompletionStep({ form, set, setC, sessions, challenge, eventId }) {
  const t = form.challenge_type;
  const c = form.completion || {};
  return (
    <div className="space-y-4" data-testid="gam-step-completion">
      {(t === "QR_CHECKIN") && <QrConfig challenge={challenge} eventId={eventId} />}
      {t === "ACTIVITY" && (
        <>
          <Field label="Linked schedule session">
            <select data-testid="gam-activity-session" value={form.linked_activity_id || ""} onChange={(e) => set({ linked_activity_id: e.target.value })} className={inp}>
              <option value="">— Select a session —</option>
              {sessions.map((s) => <option key={s.id} value={s.id}>{s.title} · {s.date}</option>)}
            </select>
          </Field>
          <Field label="Completion method">
            <select data-testid="gam-activity-method" value={c.method || "activity"} onChange={(e) => setC({ method: e.target.value })} className={inp}>
              <option value="activity">Attendance</option><option value="qr">QR scan</option><option value="manual">Manual verification</option>
            </select>
          </Field>
          {c.method === "qr" && <QrConfig challenge={challenge} eventId={eventId} />}
          <div className="text-xs text-[hsl(var(--muted-foreground))]">The existing schedule session is referenced by ID — no duplicate activity is created.</div>
        </>
      )}
      {t === "TRIVIA_POLL" && (
        <>
          <Field label="Mode">
            <div className="flex gap-2">
              {["TRIVIA", "POLL"].map((m) => <button key={m} data-testid={`gam-trivia-mode-${m}`} onClick={() => setC({ mode: m })} className={`text-xs font-semibold rounded-full px-3 py-1.5 ${(c.mode || "TRIVIA") === m ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"}`}>{m}</button>)}
            </div>
          </Field>
          <Field label="Question"><input data-testid="gam-trivia-question" value={c.question || ""} onChange={(e) => setC({ question: e.target.value })} className={inp} /></Field>
          <Field label="Answer options (2–6)">
            <div className="space-y-2">
              {(c.options || ["", ""]).map((opt, i) => (
                <div key={i} className="flex items-center gap-2">
                  {(c.mode || "TRIVIA") === "TRIVIA" && <input type="radio" data-testid={`gam-trivia-correct-${i}`} name="correct" checked={c.correct_option_index === i} onChange={() => setC({ correct_option_index: i })} title="Correct answer" />}
                  <input data-testid={`gam-trivia-option-${i}`} value={opt} onChange={(e) => { const o = [...(c.options || [])]; o[i] = e.target.value; setC({ options: o }); }} className={inp} placeholder={`Option ${i + 1}`} />
                  {(c.options || []).length > 2 && <button data-testid={`gam-trivia-remove-${i}`} onClick={() => { const o = (c.options || []).filter((_, x) => x !== i); setC({ options: o, correct_option_index: Math.min(c.correct_option_index || 0, o.length - 1) }); }} className="h-7 w-7 rounded-lg text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]"><X className="h-3.5 w-3.5" /></button>}
                </div>
              ))}
              {(c.options || []).length < 6 && <button data-testid="gam-trivia-add-option" onClick={() => setC({ options: [...(c.options || []), ""] })} className="text-xs font-semibold text-[hsl(var(--secondary))] inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add option</button>}
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Max attempts"><input data-testid="gam-trivia-attempts" type="number" min="1" value={c.max_attempts || 1} onChange={(e) => setC({ max_attempts: Number(e.target.value) || 1 })} className={inp} /></Field>
            <div className="flex items-end"><ToggleRow testid="gam-trivia-show-correct" label="Show correct after" checked={!!c.show_correct_after} onChange={(v) => setC({ show_correct_after: v })} /></div>
          </div>
          {(c.mode || "TRIVIA") === "POLL" && <div className="text-xs text-[hsl(var(--muted-foreground))]">Poll: participation qualifies — no correct answer needed.</div>}
        </>
      )}
      {t === "PHOTO" && (
        <>
          <Field label="Approval mode">
            <select data-testid="gam-photo-approval" value={c.approval_mode || "ADMIN_APPROVAL"} onChange={(e) => setC({ approval_mode: e.target.value })} className={inp}>
              <option value="ADMIN_APPROVAL">Admin approval required</option><option value="AUTO_ACCEPT">Auto-accept</option>
            </select>
          </Field>
          <ToggleRow testid="gam-photo-resubmit" label="Resubmission allowed" checked={!!c.resubmission_allowed} onChange={(v) => setC({ resubmission_allowed: v })} />
          <ToggleRow testid="gam-photo-caption" label="Caption required" checked={!!c.caption_required} onChange={(v) => setC({ caption_required: v })} />
          <Field label="Instructions (optional)"><input data-testid="gam-photo-instructions" value={c.instructions || ""} onChange={(e) => setC({ instructions: e.target.value })} className={inp} /></Field>
          <div className="text-xs text-[hsl(var(--muted-foreground))]">Photo upload &amp; review UI arrive in a later phase; this configures the data model.</div>
        </>
      )}
      {t === "EXPLORE" && (
        <>
          <Field label="Completion method">
            <select data-testid="gam-explore-method" value={c.method || "qr"} onChange={(e) => setC({ method: e.target.value })} className={inp}>
              <option value="qr">QR scan</option><option value="code">Secret code</option><option value="manual">Manual verification</option>
            </select>
          </Field>
          {c.method === "code" && <Field label="Secret code"><input data-testid="gam-explore-code" value={c.secret_code || ""} onChange={(e) => setC({ secret_code: e.target.value })} className={inp} /></Field>}
          {c.method === "qr" && <QrConfig challenge={challenge} eventId={eventId} />}
        </>
      )}
      {t === "BONUS" && (
        <>
          <ToggleRow testid="gam-bonus-multi" label="Allow multiple attendees (future)" desc="Bulk award UI arrives later; single-attendee award is the default." checked={!!c.allow_multiple_attendees} onChange={(v) => setC({ allow_multiple_attendees: v })} />
          <div className="text-xs text-[hsl(var(--muted-foreground))]">Awards will require actor + reason + timestamp (recorded in the points ledger).</div>
        </>
      )}
    </div>
  );
}

// ---- QR preview / regenerate ----------------------------------------------
function QrConfig({ challenge, eventId }) {
  const [url, setUrl] = useState(null);
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);
  const has = challenge?.has_qr;
  const load = async () => {
    if (!challenge?.id || !has) return;
    try { const blob = await api.gamQRImage(eventId, challenge.id); setUrl(URL.createObjectURL(blob)); } catch (e) {}
    try { const r = await api.gamQRLink(eventId, challenge.id); setLink(r.deep_link); } catch (e) {}
  };
  useEffect(() => { load(); return () => url && URL.revokeObjectURL(url); /* eslint-disable-next-line */ }, [challenge?.id, challenge?.qr_generation]);
  const regen = async () => { setBusy(true); try { await api.gamRegenQR(eventId, challenge.id); toast.success("QR regenerated — previous link revoked"); await load(); } catch (e) { toast.error("Regenerate failed"); } setBusy(false); };
  if (!challenge) return <div className="premium-card p-3 text-xs text-[hsl(var(--muted-foreground))]" data-testid="gam-qr-pending">A secure QR token &amp; attendee scan link are generated automatically when you save this challenge.</div>;
  return (
    <div className="premium-card p-4 flex items-center gap-4" data-testid="gam-qr-config">
      <div className="h-28 w-28 rounded-lg bg-white border border-[hsl(var(--border))] flex items-center justify-center overflow-hidden">
        {url ? <img data-testid="gam-qr-image" src={url} alt="Challenge QR" className="h-full w-full object-contain" /> : <QrCode className="h-8 w-8 text-[hsl(var(--muted-foreground))]" />}
      </div>
      <div className="min-w-0">
        <div className="text-sm font-semibold text-[hsl(var(--primary))]">Attendee scan QR</div>
        <div className="text-xs text-[hsl(var(--muted-foreground))] mb-1">Encodes a deep link carrying an opaque token (generation {challenge.qr_generation}). The attendee scanner is built in Phase 7B.</div>
        {link && <div data-testid="gam-qr-link" className="text-[11px] font-mono text-[hsl(var(--secondary))] truncate mb-2" title={link}>{link}</div>}
        <div className="flex gap-2">
          {url && <a data-testid="gam-qr-download" href={url} download={`qr-${challenge.id}.png`} className="text-xs font-semibold rounded-full border border-[hsl(var(--border))] px-3 py-1.5 text-[hsl(var(--primary))]">Download</a>}
          <button data-testid="gam-qr-regen" onClick={regen} disabled={busy} className="text-xs font-semibold rounded-full bg-[hsl(var(--muted))] px-3 py-1.5 text-[hsl(var(--primary))] inline-flex items-center gap-1 disabled:opacity-50"><RefreshCw className="h-3 w-3" /> Regenerate</button>
        </div>
      </div>
    </div>
  );
}

// ---- Review + attendee preview --------------------------------------------
function ReviewStep({ form, elig, challenge, eventId, isQrType, sessions }) {
  const T = TYPE_MAP[form.challenge_type] || {};
  const Icon = T.icon || Trophy;
  const linked = sessions.find((s) => s.id === form.linked_activity_id);
  const mysteryHiddenPreview = form.mystery_enabled;
  return (
    <div className="grid md:grid-cols-2 gap-5" data-testid="gam-step-review">
      {/* config summary */}
      <div className="space-y-2 text-sm">
        <div className="text-[11px] uppercase font-semibold text-[hsl(var(--muted-foreground))]">Configuration</div>
        <Row k="Type" v={T.label} />
        <Row k="Points" v={form.points} />
        <Row k="Audience" v={`${form.audience_mode} · ${elig ? elig.eligible_count : "…"} eligible`} />
        <Row k="Availability" v={form.start_at || form.end_at ? `${fmtDT(form.start_at)} → ${fmtDT(form.end_at)}` : "Always"} />
        <Row k="Mystery" v={form.mystery_enabled ? `Reveal ${fmtDT(form.reveal_at)}` : "No"} />
        {form.challenge_type === "ACTIVITY" && <Row k="Linked session" v={linked ? `${linked.title} (${linked.id})` : "—"} />}
        <Row k="Leaderboard" v={form.leaderboard_eligible ? "Eligible" : "No"} />
        <Row k="Featured" v={form.featured ? "Yes" : "No"} />
      </div>
      {/* attendee-style preview */}
      <div>
        <div className="text-[11px] uppercase font-semibold text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> Attendee preview (admin only)</div>
        <div className="rounded-2xl border border-[hsl(var(--border))] p-4 bg-[hsl(var(--card))]" data-testid="gam-attendee-preview">
          {mysteryHiddenPreview ? (
            <div className="text-center py-6">
              <Sparkles className="h-8 w-8 mx-auto text-[hsl(var(--secondary))]" />
              <div className="font-serif text-lg text-[hsl(var(--primary))] mt-2">Mystery Challenge</div>
              <div className="text-sm text-[hsl(var(--muted-foreground))]">Unlocks at {fmtDT(form.reveal_at)}</div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2"><div className="h-10 w-10 rounded-xl bg-[hsl(var(--secondary)/0.12)] text-[hsl(var(--secondary))] flex items-center justify-center"><Icon className="h-5 w-5" /></div>
                <div><div className="font-serif text-lg text-[hsl(var(--primary))]">{form.name || "Untitled"}</div>{form.show_points && <div className="text-xs font-semibold text-[hsl(var(--pc-gold))]">{form.points} points</div>}</div>
              </div>
              {form.short_description && <p className="text-sm text-[hsl(var(--foreground))] mt-2">{form.short_description}</p>}
              {form.full_instructions && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{form.full_instructions}</p>}
              <button disabled className="mt-3 w-full rounded-full bg-[hsl(var(--primary))] text-white px-4 py-2 text-sm font-semibold opacity-80">{ctaLabel(form.challenge_type)}</button>
            </>
          )}
        </div>
        {isQrType && challenge?.has_qr && <div className="mt-3"><QrConfig challenge={challenge} eventId={eventId} /></div>}
      </div>
    </div>
  );
}
function ctaLabel(type) {
  return { QR_CHECKIN: "Scan QR", ACTIVITY: "Check in", TRIVIA_POLL: "Answer", PHOTO: "Upload photo", EXPLORE: "Start exploring", BONUS: "Awarded by manager" }[type] || "Complete";
}
function Row({ k, v }) {
  return <div className="flex justify-between gap-3 border-b border-[hsl(var(--border))] pb-1.5"><span className="text-[hsl(var(--muted-foreground))]">{k}</span><span className="font-medium text-[hsl(var(--primary))] text-right">{String(v)}</span></div>;
}

// ---- Leaderboard (Events Manager) -----------------------------------------
const fmtActivity = (s) => (s ? String(s).replace("T", " ").slice(0, 16) : "—");

function LeaderboardTab({ eventId }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [adjustFor, setAdjustFor] = useState(null); // {attendee_id, display_name}

  const load = async (query) => {
    setLoading(true);
    try { setData(await api.gamMgrLeaderboard(eventId, query)); }
    catch (e) { toast.error("Failed to load leaderboard"); }
    setLoading(false);
  };
  useEffect(() => { load(""); /* eslint-disable-next-line */ }, [eventId]);
  useEffect(() => {
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [q]);

  const doFreeze = async () => {
    setBusy(true);
    try { await api.gamFreeze(eventId); toast.success("Leaderboard frozen"); await load(q); }
    catch (e) { toast.error("Freeze failed"); }
    setBusy(false);
  };
  const doUnfreeze = async () => {
    setBusy(true);
    try { await api.gamUnfreeze(eventId); toast.success("Leaderboard unfrozen"); await load(q); }
    catch (e) { toast.error("Unfreeze failed"); }
    setBusy(false);
  };
  const doExport = async () => {
    try {
      const blob = await api.gamLeaderboardExport(eventId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `leaderboard-${eventId}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  if (loading && !data) return <div className="premium-card p-6 animate-pulse h-40 bg-[hsl(var(--muted)/0.4)]" data-testid="gam-leaderboard-loading" />;
  const rows = data?.rows || [];

  return (
    <div data-testid="gam-leaderboard-tab">
      {/* Controls */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
          <input data-testid="gam-leaderboard-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search attendee…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-[hsl(var(--border))] text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--secondary)/0.4)]" />
        </div>
        <button data-testid="gam-adjust-points-open" onClick={() => setAdjustFor({ attendee_id: "", display_name: "" })}
          className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] px-3 py-2 text-sm font-semibold text-[hsl(var(--primary))] hover:bg-[hsl(var(--muted)/0.5)]"><Plus className="h-4 w-4" /> Adjust Points</button>
        <button data-testid="gam-leaderboard-export" onClick={doExport}
          className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] px-3 py-2 text-sm font-semibold text-[hsl(var(--primary))] hover:bg-[hsl(var(--muted)/0.5)]"><Download className="h-4 w-4" /> Export</button>
        {data?.frozen ? (
          <button data-testid="gam-leaderboard-unfreeze" disabled={busy} onClick={doUnfreeze}
            className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--secondary))] text-white px-3 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50"><Snowflake className="h-4 w-4" /> Unfreeze</button>
        ) : (
          <button data-testid="gam-leaderboard-freeze" disabled={busy} onClick={doFreeze}
            className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--primary))] text-white px-3 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50"><Snowflake className="h-4 w-4" /> Freeze</button>
        )}
      </div>

      {/* Freeze / adjustment banners */}
      {data?.frozen && (
        <div data-testid="gam-leaderboard-frozen-banner" className="mb-3 rounded-xl border border-[hsl(var(--pc-gold)/0.5)] bg-[hsl(var(--pc-gold-2)/0.15)] px-4 py-2.5 text-sm text-[hsl(38_45%_28%)] flex items-center gap-2">
          <Snowflake className="h-4 w-4" />
          <span>Frozen {fmtActivity(data.frozen_at)} ({data.freeze_source || "manual"}) by {data.frozen_by || "—"}. Final results are locked.
            {data.post_freeze_adjustment ? " Post-freeze adjustments detected — unfreeze to recalculate final standings." : ""}</span>
        </div>
      )}
      <div className="text-xs text-[hsl(var(--muted-foreground))] mb-2">
        Mode: <span className="font-semibold">{data?.mode}</span> · Scores derive from the append-only points ledger (leaderboard-eligible transactions only).
      </div>

      {rows.length === 0 ? (
        <div className="premium-card p-8 text-center text-[hsl(var(--muted-foreground))]" data-testid="gam-leaderboard-empty">
          <Medal className="h-8 w-8 mx-auto text-[hsl(var(--pc-gold)/0.5)]" />
          <p className="mt-2 text-sm">No ranked participants yet.</p>
        </div>
      ) : (
        <div className="premium-card overflow-x-auto">
          <table className="w-full text-sm" data-testid="gam-leaderboard-table">
            <thead>
              <tr className="text-left text-[hsl(var(--muted-foreground))] border-b border-[hsl(var(--border))]">
                <th className="py-2 px-3 font-semibold">Rank</th>
                <th className="py-2 px-3 font-semibold">Attendee</th>
                <th className="py-2 px-3 font-semibold text-right">Points</th>
                <th className="py-2 px-3 font-semibold text-right">Completions</th>
                <th className="py-2 px-3 font-semibold text-right">Badges</th>
                <th className="py-2 px-3 font-semibold">Last activity</th>
                <th className="py-2 px-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.attendee_id} data-testid="gam-leaderboard-row" className="border-b border-[hsl(var(--border))] last:border-0 hover:bg-[hsl(var(--muted)/0.35)]">
                  <td className="py-2 px-3">
                    <span className={`font-serif font-bold ${r.rank === 1 ? "text-[hsl(45_80%_45%)]" : r.rank <= 3 ? "text-[hsl(var(--secondary))]" : "text-[hsl(var(--primary))]"}`}>#{r.rank}</span>
                  </td>
                  <td className="py-2 px-3 font-medium text-[hsl(var(--primary))]">
                    {r.display_name}
                    {r.post_freeze_delta ? <span className="ml-2 text-[10px] font-bold text-[hsl(var(--secondary))]">post-freeze {r.post_freeze_delta > 0 ? "+" : ""}{r.post_freeze_delta}</span> : null}
                  </td>
                  <td className="py-2 px-3 text-right font-bold text-[hsl(var(--pc-gold))]">{r.points}{r.personal_points !== r.points ? <span className="ml-1 text-[10px] font-normal text-[hsl(var(--muted-foreground))]">({r.personal_points} total)</span> : null}</td>
                  <td className="py-2 px-3 text-right">{r.eligible_completions}{r.total_completions !== r.eligible_completions ? <span className="text-[10px] text-[hsl(var(--muted-foreground))]">/{r.total_completions}</span> : null}</td>
                  <td className="py-2 px-3 text-right">{r.badge_count}</td>
                  <td className="py-2 px-3 text-[hsl(var(--muted-foreground))]">{fmtActivity(r.last_activity)}</td>
                  <td className="py-2 px-3 text-right whitespace-nowrap">
                    <button data-testid="gam-leaderboard-view-detail" onClick={() => setDetailId(r.attendee_id)} className="p-1.5 rounded-lg hover:bg-[hsl(var(--muted))]" title="View game detail"><Eye className="h-4 w-4 text-[hsl(var(--secondary))]" /></button>
                    <button data-testid="gam-leaderboard-adjust-row" onClick={() => setAdjustFor({ attendee_id: r.attendee_id, display_name: r.display_name })} className="p-1.5 rounded-lg hover:bg-[hsl(var(--muted))]" title="Adjust points"><Plus className="h-4 w-4 text-[hsl(var(--primary))]" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detailId && <GameDetailDrawer eventId={eventId} attendeeId={detailId} onClose={() => setDetailId(null)} />}
      {adjustFor && <AdjustPointsDialog eventId={eventId} preset={adjustFor} onClose={() => setAdjustFor(null)} onSaved={() => { setAdjustFor(null); load(q); }} />}
    </div>
  );
}

function AdjustPointsDialog({ eventId, preset, onClose, onSaved }) {
  const [attendeeId, setAttendeeId] = useState(preset.attendee_id || "");
  const [points, setPoints] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [idem] = useState(() => `mgr-adjust:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`);

  const submit = async () => {
    if (!attendeeId.trim()) return toast.error("Attendee id is required");
    const n = parseInt(points, 10);
    if (!n || Number.isNaN(n)) return toast.error("Enter a non-zero whole number");
    if (!reason.trim()) return toast.error("A reason is required");
    setBusy(true);
    try {
      await api.gamAdjustPoints(eventId, { attendee_id: attendeeId.trim(), points: n, reason: reason.trim(), idempotency_key: idem });
      toast.success("Points adjusted");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Adjustment failed"); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[120] grid place-items-center bg-black/50 p-4" data-testid="gam-adjust-dialog">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-lg text-[hsl(var(--primary))]">Adjust Points</h3>
          <button data-testid="gam-adjust-close" onClick={onClose} className="p-1.5 rounded-lg hover:bg-[hsl(var(--muted))]"><X className="h-4 w-4" /></button>
        </div>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">Creates a new audited ledger transaction. Prior transactions are never edited. Use a negative value to deduct.</p>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-[hsl(var(--primary))]">Attendee ID</label>
            <input data-testid="gam-adjust-attendee" value={attendeeId} onChange={(e) => setAttendeeId(e.target.value)} placeholder="att-tm-…" disabled={!!preset.attendee_id}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] text-sm disabled:bg-[hsl(var(--muted)/0.4)]" />
            {preset.display_name && <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{preset.display_name}</div>}
          </div>
          <div>
            <label className="text-xs font-semibold text-[hsl(var(--primary))]">Points (+/-)</label>
            <input data-testid="gam-adjust-points" type="number" value={points} onChange={(e) => setPoints(e.target.value)} placeholder="e.g. 50 or -25"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[hsl(var(--primary))]">Reason (required)</label>
            <textarea data-testid="gam-adjust-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Why is this adjustment being made?"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] text-sm" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-sm font-semibold text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]">Cancel</button>
          <button data-testid="gam-adjust-submit" disabled={busy} onClick={submit} className="px-4 py-2 rounded-full bg-[hsl(var(--primary))] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50">Apply</button>
        </div>
      </div>
    </div>
  );
}

function GameDetailDrawer({ eventId, attendeeId, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    let ok = true;
    api.gamAttendeeGame(eventId, attendeeId).then((r) => ok && setD(r)).catch(() => ok && setD({ error: true }));
    return () => { ok = false; };
  }, [eventId, attendeeId]);

  return (
    <div className="fixed inset-0 z-[120] flex justify-end bg-black/40" data-testid="gam-game-detail" onClick={onClose}>
      <div className="bg-white w-full max-w-md h-full overflow-y-auto p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-lg text-[hsl(var(--primary))]">Attendee Game Detail</h3>
          <button data-testid="gam-game-detail-close" onClick={onClose} className="p-1.5 rounded-lg hover:bg-[hsl(var(--muted))]"><X className="h-4 w-4" /></button>
        </div>
        {!d ? <div className="animate-pulse h-24 bg-[hsl(var(--muted)/0.4)] rounded-xl" /> : d.error ? (
          <p className="text-sm text-[hsl(var(--destructive,0_70%_50%))]">Failed to load.</p>
        ) : (
          <>
            <div className="premium-card p-3 mb-3">
              <div className="font-semibold text-[hsl(var(--primary))]">{d.attendee?.display_name}</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">{d.attendee?.email}</div>
              <div className="flex gap-4 mt-2 text-sm">
                <span><b className="text-[hsl(var(--pc-gold))]">{d.competitive_points}</b> competitive</span>
                <span><b>{d.personal_points}</b> total</span>
              </div>
            </div>
            <h4 className="text-xs font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">Completions ({d.completions?.length || 0})</h4>
            <div className="space-y-1.5 mb-4">
              {(d.completions || []).map((c) => (
                <div key={c.id} className={`text-xs flex justify-between border-b border-[hsl(var(--border))] pb-1 ${c.voided ? "opacity-50 line-through" : ""}`}>
                  <span>{c.completion_method} · +{c.points_awarded}</span>
                  <span className="text-[hsl(var(--muted-foreground))]">{fmtActivity(c.created_at)}{c.voided ? " (reversed)" : ""}</span>
                </div>
              ))}
              {(!d.completions || d.completions.length === 0) && <div className="text-xs text-[hsl(var(--muted-foreground))]">None</div>}
            </div>
            <h4 className="text-xs font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">Ledger transactions ({d.transactions?.length || 0})</h4>
            <div className="space-y-1.5">
              {(d.transactions || []).map((t) => (
                <div key={t.id} className="text-xs flex justify-between border-b border-[hsl(var(--border))] pb-1">
                  <span className={t.points_delta < 0 ? "text-[hsl(0_60%_45%)]" : ""}>{t.points_delta > 0 ? "+" : ""}{t.points_delta} · {t.source_type}</span>
                  <span className="text-[hsl(var(--muted-foreground))]">{fmtActivity(t.created_at)}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---- Badges (Events Manager) ----------------------------------------------
const BADGE_ICON_MGR = { medal: Medal, crown: Crown, sparkles: Sparkles, compass: Compass, brain: Brain, "trending-up": TrendingUp, star: Star, award: Award, lock: Lock, trophy: Trophy };
const RULE_LABEL = {
  FIRST_COMPLETION: "First completion",
  SPECIFIC_CHALLENGE: "Specific challenge",
  TOTAL_COMPLETIONS: "Total completions",
  POINTS_THRESHOLD: "Points threshold",
  ALL_OF_CHALLENGES: "All of challenges",
};

function BadgesTab({ eventId, challenges }) {
  const [badges, setBadges] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // {mode, badge?}
  const [revokeFor, setRevokeFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setBadges((await api.gamMgrBadges(eventId)).badges); }
    catch (e) { toast.error("Failed to load badges"); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eventId]);

  const toggleActive = async (b) => {
    try {
      if (!b.active) {
        const detail = await api.gamBadgeDetail(eventId, b.id);
        if (detail.currently_qualifying > 0 &&
            !window.confirm(`${detail.currently_qualifying} attendee(s) already qualify. Activate now? You can then run "Evaluate existing attendees" to award them.`)) return;
      }
      await api.gamBadgeActive(eventId, b.id, !b.active);
      toast.success(!b.active ? "Badge activated" : "Badge deactivated");
      load();
    } catch (e) { toast.error("Failed"); }
  };
  const evaluate = async (b) => {
    try {
      const r = await api.gamBadgeEvaluate(eventId, b.id);
      toast.success(`Evaluated: ${r.newly_awarded} newly awarded, ${r.already_awarded} already held`);
      load();
    } catch (e) { toast.error("Evaluation failed"); }
  };

  if (loading && !badges) return <div className="premium-card p-6 animate-pulse h-40 bg-[hsl(var(--muted)/0.4)]" data-testid="gam-badges-loading" />;

  return (
    <div data-testid="gam-badges-tab">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-[hsl(var(--muted-foreground))]">Achievements are separate from points — earning a badge awards no points.</p>
        <button data-testid="gam-badge-create" onClick={() => setEditor({ mode: "create" })}
          className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--primary))] text-white px-4 py-2 text-sm font-semibold hover:opacity-90"><Plus className="h-4 w-4" /> Create Badge</button>
      </div>

      {(!badges || badges.length === 0) ? (
        <div className="premium-card p-8 text-center text-[hsl(var(--muted-foreground))]" data-testid="gam-badges-empty">
          <Medal className="h-8 w-8 mx-auto text-[hsl(var(--pc-gold)/0.5)]" /><p className="mt-2 text-sm">No badges yet. Create your first achievement.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {badges.map((b) => {
            const Bi = BADGE_ICON_MGR[b.icon] || Medal;
            return (
              <div key={b.id} data-testid="gam-badge-card" className="premium-card p-4 flex items-start gap-3">
                <div className={`h-12 w-12 shrink-0 rounded-full grid place-items-center border ${b.active ? "border-[hsl(var(--pc-gold)/0.6)] text-[hsl(var(--pc-gold))] bg-[hsl(var(--pc-gold)/0.1)]" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"}`}><Bi className="h-6 w-6" /></div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[hsl(var(--primary))]">{b.name}</span>
                    {b.mystery && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-[hsl(var(--secondary)/0.15)] text-[hsl(var(--secondary))]">Mystery</span>}
                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${b.active ? "bg-[hsl(140_40%_92%)] text-[hsl(140_50%_30%)]" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"}`}>{b.active ? "Active" : "Inactive"}</span>
                  </div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{RULE_LABEL[b.rule_type]} · {b.awarded_count} awarded</div>
                  {b.description && <div className="text-xs text-[hsl(var(--primary)/0.8)] mt-1">{b.description}</div>}
                  <div className="flex gap-1.5 mt-2 flex-wrap">
                    <button data-testid="gam-badge-edit" onClick={() => setEditor({ mode: "edit", badge: b })} className="text-xs px-2 py-1 rounded-lg border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] inline-flex items-center gap-1"><Pencil className="h-3 w-3" /> Edit</button>
                    <button data-testid="gam-badge-toggle" onClick={() => toggleActive(b)} className="text-xs px-2 py-1 rounded-lg border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]">{b.active ? "Deactivate" : "Activate"}</button>
                    <button data-testid="gam-badge-evaluate" onClick={() => evaluate(b)} className="text-xs px-2 py-1 rounded-lg border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] inline-flex items-center gap-1"><RefreshCw className="h-3 w-3" /> Evaluate existing</button>
                    <button data-testid="gam-badge-revoke-open" onClick={() => setRevokeFor(b)} className="text-xs px-2 py-1 rounded-lg border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] text-[hsl(0_60%_45%)]">Revoke</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editor && <BadgeBuilder eventId={eventId} challenges={challenges} editor={editor} onClose={() => setEditor(null)} onSaved={() => { setEditor(null); load(); }} />}
      {revokeFor && <BadgeRevokeDialog eventId={eventId} badge={revokeFor} onClose={() => setRevokeFor(null)} onDone={() => { setRevokeFor(null); load(); }} />}
    </div>
  );
}

function BadgeBuilder({ eventId, challenges, editor, onClose, onSaved }) {
  const b = editor.badge || {};
  const [name, setName] = useState(b.name || "");
  const [description, setDescription] = useState(b.description || "");
  const [icon, setIcon] = useState(b.icon || "medal");
  const [ruleType, setRuleType] = useState(b.rule_type || "FIRST_COMPLETION");
  const [rc, setRc] = useState(b.rule_config || {});
  const [visible, setVisible] = useState(b.visible_before_earning !== false);
  const [mystery, setMystery] = useState(!!b.mystery);
  const [busy, setBusy] = useState(false);
  const published = (challenges || []).filter((c) => c.publication_state === "PUBLISHED");

  const set = (k, v) => setRc((p) => ({ ...p, [k]: v }));
  const toggleChallenge = (id) => setRc((p) => {
    const ids = new Set(p.challenge_ids || []);
    ids.has(id) ? ids.delete(id) : ids.add(id);
    return { ...p, challenge_ids: [...ids] };
  });

  const submit = async () => {
    if (!name.trim()) return toast.error("Badge name is required");
    setBusy(true);
    const payload = { name: name.trim(), description: description.trim(), icon, rule_type: ruleType, rule_config: rc, visible_before_earning: visible, mystery };
    try {
      if (editor.mode === "create") await api.gamCreateBadge(eventId, payload);
      else await api.gamUpdateBadge(eventId, b.id, payload);
      toast.success("Badge saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  const ICONS = ["medal", "crown", "sparkles", "compass", "brain", "trending-up", "star", "award", "trophy"];
  const PreviewIcon = BADGE_ICON_MGR[icon] || Medal;

  return (
    <div className="fixed inset-0 z-[120] grid place-items-center bg-black/50 p-4" data-testid="gam-badge-builder">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-lg text-[hsl(var(--primary))]">{editor.mode === "create" ? "Create Badge" : "Edit Badge"}</h3>
          <button data-testid="gam-badge-builder-close" onClick={onClose} className="p-1.5 rounded-lg hover:bg-[hsl(var(--muted))]"><X className="h-4 w-4" /></button>
        </div>

        {/* Attendee preview */}
        <div className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-[hsl(var(--muted)/0.4)]">
          <div className="h-12 w-12 rounded-full grid place-items-center border border-[hsl(var(--pc-gold)/0.6)] text-[hsl(var(--pc-gold))] bg-[hsl(var(--pc-gold)/0.1)]">{mystery ? <Lock className="h-6 w-6" /> : <PreviewIcon className="h-6 w-6" />}</div>
          <div><div className="text-xs text-[hsl(var(--muted-foreground))]">Attendee preview</div><div className="font-semibold text-[hsl(var(--primary))]">{mystery ? "Mystery Badge" : (name || "Badge name")}</div></div>
        </div>

        <div className="space-y-3">
          <div><label className="text-xs font-semibold text-[hsl(var(--primary))]">Name</label>
            <input data-testid="gam-badge-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] text-sm" /></div>
          <div><label className="text-xs font-semibold text-[hsl(var(--primary))]">Description</label>
            <textarea data-testid="gam-badge-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="mt-1 w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] text-sm" /></div>
          <div><label className="text-xs font-semibold text-[hsl(var(--primary))]">Icon</label>
            <div className="flex gap-2 flex-wrap mt-1">
              {ICONS.map((ic) => { const I = BADGE_ICON_MGR[ic]; return (
                <button key={ic} type="button" data-testid={`gam-badge-icon-${ic}`} onClick={() => setIcon(ic)} className={`h-9 w-9 grid place-items-center rounded-lg border ${icon === ic ? "border-[hsl(var(--pc-gold))] bg-[hsl(var(--pc-gold)/0.1)] text-[hsl(var(--pc-gold))]" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"}`}><I className="h-4 w-4" /></button>
              ); })}
            </div>
          </div>
          <div><label className="text-xs font-semibold text-[hsl(var(--primary))]">Rule type</label>
            <select data-testid="gam-badge-rule-type" value={ruleType} onChange={(e) => { setRuleType(e.target.value); setRc({}); }} className="mt-1 w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] text-sm bg-white">
              {Object.entries(RULE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>

          {/* Dynamic rule config */}
          {ruleType === "SPECIFIC_CHALLENGE" && (
            <div><label className="text-xs font-semibold text-[hsl(var(--primary))]">Challenge</label>
              <select data-testid="gam-badge-challenge" value={rc.challenge_id || ""} onChange={(e) => set("challenge_id", e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] text-sm bg-white">
                <option value="">Select challenge…</option>
                {published.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select></div>
          )}
          {ruleType === "TOTAL_COMPLETIONS" && (
            <div><label className="text-xs font-semibold text-[hsl(var(--primary))]">Required completions</label>
              <input data-testid="gam-badge-count" type="number" min="1" value={rc.count || ""} onChange={(e) => set("count", e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] text-sm" /></div>
          )}
          {ruleType === "POINTS_THRESHOLD" && (
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs font-semibold text-[hsl(var(--primary))]">Points threshold</label>
                <input data-testid="gam-badge-threshold" type="number" min="1" value={rc.threshold || ""} onChange={(e) => set("threshold", e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] text-sm" /></div>
              <div><label className="text-xs font-semibold text-[hsl(var(--primary))]">Basis</label>
                <select data-testid="gam-badge-basis" value={rc.points_basis || "competitive"} onChange={(e) => set("points_basis", e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] text-sm bg-white">
                  <option value="competitive">Leaderboard points</option><option value="personal">Total points</option></select></div>
            </div>
          )}
          {ruleType === "ALL_OF_CHALLENGES" && (
            <div><label className="text-xs font-semibold text-[hsl(var(--primary))]">Challenges (all required)</label>
              <div className="mt-1 space-y-1 max-h-40 overflow-y-auto border border-[hsl(var(--border))] rounded-lg p-2">
                {published.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" data-testid={`gam-badge-allof-${c.id}`} checked={(rc.challenge_ids || []).includes(c.id)} onChange={() => toggleChallenge(c.id)} />{c.name}</label>
                ))}
              </div></div>
          )}

          <div className="flex items-center gap-4 pt-1">
            <label className="flex items-center gap-2 text-sm text-[hsl(var(--primary))]"><input type="checkbox" data-testid="gam-badge-visible" checked={visible} onChange={(e) => setVisible(e.target.checked)} /> Visible before earning</label>
            <label className="flex items-center gap-2 text-sm text-[hsl(var(--primary))]"><input type="checkbox" data-testid="gam-badge-mystery" checked={mystery} onChange={(e) => setMystery(e.target.checked)} /> Mystery</label>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-sm font-semibold text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]">Cancel</button>
          <button data-testid="gam-badge-save" disabled={busy} onClick={submit} className="px-4 py-2 rounded-full bg-[hsl(var(--primary))] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50">Save Badge</button>
        </div>
      </div>
    </div>
  );
}

function BadgeRevokeDialog({ eventId, badge, onClose, onDone }) {
  const [attendeeId, setAttendeeId] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!attendeeId.trim()) return toast.error("Attendee id is required");
    if (!reason.trim()) return toast.error("A reason is required");
    setBusy(true);
    try { await api.gamBadgeRevoke(eventId, badge.id, { attendee_id: attendeeId.trim(), reason: reason.trim() }); toast.success("Badge revoked"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Revoke failed"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[121] grid place-items-center bg-black/50 p-4" data-testid="gam-badge-revoke">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5">
        <div className="flex items-center justify-between mb-3"><h3 className="font-serif text-lg text-[hsl(var(--primary))]">Revoke "{badge.name}"</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[hsl(var(--muted))]"><X className="h-4 w-4" /></button></div>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">The original award is preserved (audited) and marked revoked. Use this only for an erroneous award.</p>
        <label className="text-xs font-semibold text-[hsl(var(--primary))]">Attendee ID</label>
        <input data-testid="gam-badge-revoke-attendee" value={attendeeId} onChange={(e) => setAttendeeId(e.target.value)} placeholder="att-tm-…" className="mt-1 w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] text-sm" />
        <label className="text-xs font-semibold text-[hsl(var(--primary))] mt-3 block">Reason</label>
        <textarea data-testid="gam-badge-revoke-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="mt-1 w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] text-sm" />
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-sm font-semibold text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]">Cancel</button>
          <button data-testid="gam-badge-revoke-submit" disabled={busy} onClick={submit} className="px-4 py-2 rounded-full bg-[hsl(0_60%_45%)] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50">Revoke</button>
        </div>
      </div>
    </div>
  );
}
